import React, { useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { noop } from 'lodash';
import { bindActionCreators } from 'redux';

import fetchSalon from 'actions/fetchSalon.action';
import beInitReaderPayment from 'actions/pos/beInitReaderPayment.action';
import feSetPosIntentId from 'actions/pos/feSetPosIntentId.action';
import determineAndStorePaymentIntentId from 'bl/pos/determineAndStorePaymentIntentId.bl';
import CheckoutFooter from 'components/checkout/CheckoutFooter.component';
import DialogMessage from 'components/dialog/DialogMessage.component';
import MoneyPanel from 'components/experiment/MoneyPanel.component';
import PosReaderDialog from 'components/pos/PosReaderDialog.component';
import PRDS from 'components/pos/PosReaderDialogStatus.const';
import PosReaderPriceLine from 'components/pos/PosReaderPriceLine.component';
import PosStateMessage from 'components/pos/PosStateMessage.component';
import PAYMENT_ORIGIN_POS from 'constants/payment/paymentOriginPos.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { selectCheckoutPos } from 'selectors/checkout.selectors';
import { selectCurrentSalonFlags, selectCurrentSalonPosFlow } from 'selectors/currentSalon.selectors';
import asArray from 'utils/arrays/asArray.util';
import { fromEventToValue } from 'utils/converters/event.util';
import { fromPropsToMatchedSalonId } from 'utils/converters/props/bl.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import CN from './PosPage.module.scss';

const L = logger('PosPage');

const ENABLE_EXTRA_INFO = false;

const determineLocaleDecimalSeparator = () => (1.2).toLocaleString().substring(1, 2);

const ITEMS = Object.freeze([
  {
    description: 'Service One',
    price: 3.0,
    amount: 6.0,
    quantity: 2,
    total: 1,
    discount: 1,
    discountType: 'absolute',
  },
  {
    description: 'Service Two',
    price: 33.0,
    amount: 66.0,
    quantity: 22,
    total: 11,
    discount: 11,
    discountType: 'percentage',
  },
]);

const PosPage = (props) => {
  const { beInitReaderPayment, feSetPosIntentId } = props;

  const salonId = fromPropsToMatchedSalonId(props);
  const pos = asObject(props.pos);
  const clientId = 59797;
  const appointmentId = 88464;
  const amount = 6.0;

  const { val: isOpen, tip: toggleDialog, nay: closeDialog } = useTip(false);
  const [posInfo, setPosInfo] = useStateWhileMounted(asObject());
  const [price, setPrice] = useStateWhileMounted('0.00');
  const inputRef = useRef();
  const [value, setValue] = useStateWhileMounted();

  const handlePriceChange = useCallback((ev) => void setPrice(fromEventToValue(ev)), [setPrice]);

  const handleChange = useCallback(
    (ev) => {
      const info = asObject(L.debug('handleChange()', 'ev:', ev));
      setPosInfo(info);

      if (info.status === PRDS.cancel || info.status === PRDS.closed) {
        closeDialog();
      }
    },
    [closeDialog, setPosInfo],
  );

  const handleAction = useCallback(
    (options) =>
      determineAndStorePaymentIntentId({
        readerId: options.readerId,
        pos,
        salonId,
        clientId,
        appointmentId,
        amount,
        items: ITEMS,
        paymentOrigin: PAYMENT_ORIGIN_POS.checkout,
        beInitReaderPayment,
        feSetPosIntentId,
      }),
    [beInitReaderPayment, feSetPosIntentId, pos, salonId],
  );

  useEffect(() => {
    const element = inputRef.current;
    const value = element ? element.value : void 1;
    setValue(value);
    L.debug('useEffect()', element, value);
  }, [setValue]);

  L.debug('()', { salonId, isOpen }, props);

  return (
    <div data-bem="PosPage" className={CN.component}>
      <div>
        <p>
          Some info.
          <br />
          Locale decimal separator is {determineLocaleDecimalSeparator()}
          <br />
          but in inputs you will see {value} ( <input ref={inputRef} value={1234.567} readOnly /> )
        </p>
        <input onChange={handlePriceChange} value={price} />
        <button type="button" onClick={toggleDialog}>
          toggle
        </button>
        {isOpen && (
          <PosReaderDialog open onChange={handleChange} onAction={handleAction}>
            <DialogMessage>
              <PosStateMessage posStep={posInfo.status} code={posInfo.code} price={price || 'unknown price'} />
            </DialogMessage>
            {ENABLE_EXTRA_INFO && (
              <>
                {asArray(ITEMS).map(({ clientId, productId, serviceId, ...extra }, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <PosReaderPriceLine {...extra} key={index} />
                ))}
                <PosReaderPriceLine description="--- --- --- ---" />
                <PosReaderPriceLine description="Total" total={2000} />
              </>
            )}
          </PosReaderDialog>
        )}
      </div>

      <MoneyPanel amount="123.00" />
      <MoneyPanel amount="123.00" displayDecimals="mixed" />

      <MoneyPanel amount={123.456} />
      <MoneyPanel amount={-123.456} />
      <MoneyPanel amount={-123.456} noNegative />

      <CheckoutFooter card="9876" balance={4321} checkoutOption={null} blockComplete otherAmount={1234} onComplete={noop} chargeCardOnFile={false} />
    </div>
  );
};

const mapStateToProps = (state) => ({
  posFlow: selectCurrentSalonPosFlow(state),
  salonFlags: selectCurrentSalonFlags(state),
  pos: selectCheckoutPos(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchSalon,
      beInitReaderPayment,
      feSetPosIntentId,
    },
    dispatch,
  );

const PosPageHoc = connect(mapStateToProps, mapDispatchToProps)(PosPage);

// noinspection JSUnusedGlobalSymbols
export default PosPageHoc;
